import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import type { RootState, AppDispatch } from '../../store/store';
import { applyAuthenticatedUser } from '../../store/authSlice';
import { adoptSharedSession } from '../../services/adoptSharedSession';
import { readSharedAuthCookieIncludingCheckout } from '../../services/cookieAuth';

/**
 * Before protected routes decide the visitor is logged out, adopt a product-link
 * or mailbox cookie that lives on this host but not in this app's localStorage.
 */
export default function SharedSessionBridge({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const userId = useSelector((state: RootState) => state.auth.user?.userId ?? '');
  const shared = readSharedAuthCookieIncludingCheckout();
  const needsAdopt = Boolean(shared && (!isAuthenticated || shared.userId.toLowerCase() !== userId.toLowerCase()));
  const [ready, setReady] = useState(!needsAdopt);
  const attempted = useRef(false);

  useEffect(() => {
    if (!needsAdopt || attempted.current) {
      setReady(true);
      return;
    }
    attempted.current = true;
    let cancelled = false;
    void adoptSharedSession()
      .then((user) => {
        if (cancelled) return;
        if (user) dispatch(applyAuthenticatedUser(user));
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, needsAdopt]);

  if (!ready) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
}
